import {
  indexCatalogByCode,
  resolveCatalogLabel,
  resolveCatalogLabels,
  type CatalogId,
  type CatalogIndex,
} from '@/shared/utils/catalog-label.util';
import { useCallback, useMemo } from 'react';
import { useBusinessActivities } from '../business/use-business-activities.hook';
import { useFireProtectionRatingTypes } from '../business/use-fire-protection-rating-types.hook';
import { useTheftProtectionRatingTypes } from '../business/use-theft-protection-rating-types.hook';
import { useConstructionTypes } from '../homeowner/use-construction-types.hook';
import { useInsuredObjects } from '../homeowner/use-insured-objects.hook';
import { usePropertyClassifications } from '../homeowner/use-property-classifications.hook';

/* eslint-disable no-unused-vars */
export type CatalogLabelResolver = {
  /** Traduz um código. Cai no código cru enquanto o catálogo não chegou. */
  resolve: (
    _catalogId: CatalogId,
    _code: string | number | null | undefined,
    _fallbackDescription?: string
  ) => string;
  /** Traduz uma lista de códigos num texto único, sem repetições. */
  resolveAll: (
    _catalogId: CatalogId,
    _codes: ReadonlyArray<string | number | null | undefined>
  ) => string;
  /** `true` enquanto algum dos catálogos pedidos ainda está carregando. */
  isLoading: boolean;
};
/* eslint-enable no-unused-vars */

/**
 * Carrega apenas os catálogos pedidos e devolve um resolvedor de rótulos.
 *
 * Reaproveita os hooks de catálogo que já existem (mesmas `queryKey`s, mesmo
 * `staleTime`), então não há request novo: se a tela de cotação ou de apólice
 * já buscou o catálogo, o TanStack Query entrega do cache. Os catálogos não
 * pedidos ficam `enabled: false` — um produto que não usa atividade
 * empresarial não baixa a lista de atividades.
 *
 * Chame no **componente pai** e passe os rótulos por props: instanciar dentro
 * de um card que se repete por local criaria N observers e N índices do mesmo
 * catálogo.
 */
export function useCatalogLabels(
  catalogIds: readonly CatalogId[]
): CatalogLabelResolver {
  // A identidade do array muda a cada render quando o chamador passa um
  // literal; a chave ordenada mantém os memos estáveis sem exigir que todo
  // chamador memoize.
  const requestedKey = [...catalogIds].sort().join('|');
  const requested = useMemo(
    () => new Set(requestedKey ? (requestedKey.split('|') as CatalogId[]) : []),
    [requestedKey]
  );

  const businessActivities = useBusinessActivities({
    enabled: requested.has('business-activities'),
  });
  const constructionTypes = useConstructionTypes({
    enabled: requested.has('construction-types'),
  });
  const insuredObjects = useInsuredObjects({
    enabled: requested.has('insured-objects'),
  });
  const propertyClassifications = usePropertyClassifications({
    enabled: requested.has('property-classifications'),
  });
  const fireProtectionRatingTypes = useFireProtectionRatingTypes({
    enabled: requested.has('fire-protection-rating-types'),
  });
  const theftProtectionRatingTypes = useTheftProtectionRatingTypes({
    enabled: requested.has('theft-protection-rating-types'),
  });

  const indexes = useMemo<Record<CatalogId, CatalogIndex>>(
    () => ({
      'business-activities': indexCatalogByCode(businessActivities.data),
      'construction-types': indexCatalogByCode(constructionTypes.data),
      'insured-objects': indexCatalogByCode(insuredObjects.data),
      'property-classifications': indexCatalogByCode(
        propertyClassifications.data
      ),
      'fire-protection-rating-types': indexCatalogByCode(
        fireProtectionRatingTypes.data
      ),
      'theft-protection-rating-types': indexCatalogByCode(
        theftProtectionRatingTypes.data
      ),
    }),
    [
      businessActivities.data,
      constructionTypes.data,
      insuredObjects.data,
      propertyClassifications.data,
      fireProtectionRatingTypes.data,
      theftProtectionRatingTypes.data,
    ]
  );

  const resolve = useCallback<CatalogLabelResolver['resolve']>(
    (catalogId, code, fallbackDescription) =>
      resolveCatalogLabel(indexes[catalogId], code, fallbackDescription),
    [indexes]
  );

  const resolveAll = useCallback<CatalogLabelResolver['resolveAll']>(
    (catalogId, codes) => resolveCatalogLabels(indexes[catalogId], codes),
    [indexes]
  );

  const isLoading =
    businessActivities.isLoading ||
    constructionTypes.isLoading ||
    insuredObjects.isLoading ||
    propertyClassifications.isLoading ||
    fireProtectionRatingTypes.isLoading ||
    theftProtectionRatingTypes.isLoading;

  return { resolve, resolveAll, isLoading };
}
